import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth, USER_ROLES } from '../auth/AuthContext';
import { getPostLoginPath } from '../auth/loginNavigation';
import { toast } from 'react-toastify';
import { FcGoogle } from 'react-icons/fc';
import { FaFacebook } from 'react-icons/fa';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID;

const LoginIntegrated = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, googleLogin, facebookLogin, isAuthenticated, error, clearError } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Clear error messages
  useEffect(() => {
    return () => clearError?.();
  }, [clearError]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    if (window.google?.accounts?.id) return;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!FACEBOOK_APP_ID) return;
    if (window.FB) return;
    if (document.querySelector('script[src*="connect.facebook.net"]')) return;
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v18.0'
      });
    };
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  const handleGoogleLogin = useCallback(() => {
    clearError?.();
    if (!GOOGLE_CLIENT_ID) {
      toast.error('Google Sign-In not configured');
      return;
    }
    if (!window.google?.accounts?.id) {
      toast.error('Google Sign-In is loading. Try again in a moment.');
      return;
    }
    setLoading(true);
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async (response) => {
        try {
          const result = await googleLogin(response.credential);
          const u = result?.user;
          navigate(getPostLoginPath(u, { from: location.state?.from }), { replace: true });
          toast.success('Signed in with Google');
        } catch (err) {
          toast.error(err.message || 'Google sign-in failed');
        } finally {
          setLoading(false);
        }
      }
    });
    window.google.accounts.id.prompt();
  }, [googleLogin, navigate, location.state?.from, clearError]);

  const handleFacebookLogin = useCallback(() => {
    clearError?.();
    if (!FACEBOOK_APP_ID) {
      toast.error('Facebook sign-in not configured');
      return;
    }
    if (!window.FB) {
      toast.error('Facebook is loading. Try again in a moment.');
      return;
    }
    setLoading(true);
    window.FB.login(
      async (response) => {
        try {
          if (response.authResponse?.accessToken) {
            const result = await facebookLogin(response.authResponse.accessToken);
            const u = result?.user;
            navigate(getPostLoginPath(u, { from: location.state?.from }), { replace: true });
            toast.success('Signed in with Facebook');
          } else {
            toast.error('Facebook sign-in was cancelled or failed');
          }
        } catch (err) {
          toast.error(err.message || 'Facebook sign-in failed');
        } finally {
          setLoading(false);
        }
      },
      { scope: 'email,public_profile' }
    );
  }, [facebookLogin, navigate, location.state?.from, clearError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      const user = result?.user;
      toast.success('Signed in successfully');
      navigate(getPostLoginPath(user, { from: location.state?.from }), { replace: true });
      return;
    } catch (loginErr) {
      // Login failed: try register (new user). If register fails = email exists = wrong password.
      try {
        const name = formData.email.split('@')[0] || 'User';
        await register(name, formData.email, formData.password, USER_ROLES.CUSTOMER);
        toast.success('Welcome! You can start shopping.');
        navigate('/', { replace: true });
        return;
      } catch (registerErr) {
        const regMsg = (registerErr?.message || '').toLowerCase();
        if (regMsg.includes('already') || regMsg.includes('exist') || regMsg.includes('registered')) {
          toast.error('Invalid email or password. Try again or use Forgot password.');
        } else {
          toast.error(registerErr?.message || 'Something went wrong. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Continue with email</h1>
          <p className="text-gray-600">Sign in if you have an account, or we’ll create one for you</p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
              disabled={loading}
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-600">Remember me</span>
            </label>
            <a href="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700">
              Forgot password?
            </a>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
          >
            {loading ? 'Please wait...' : 'Continue'}
          </button>
        </form>

        {/* Divider */}
        <div className="my-6 flex items-center">
          <div className="flex-1 border-t border-gray-300"></div>
          <span className="px-3 text-gray-500 text-sm">or</span>
          <div className="flex-1 border-t border-gray-300"></div>
        </div>

        {/* Google Login */}
        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-lg transition disabled:opacity-60"
        >
          <FcGoogle className="text-xl" />
          Sign in with Google
        </button>

        {/* Facebook Login */}
        <button
          type="button"
          onClick={handleFacebookLogin}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-lg transition disabled:opacity-60 mt-2"
        >
          <FaFacebook className="text-xl text-blue-600" />
          Sign in with Facebook
        </button>

        <p className="text-center text-gray-500 text-xs mt-4">
          <Link to="/login" className="text-blue-600 hover:text-blue-700">Sign in with mobile number</Link>
        </p>

        <p className="text-center text-gray-600 text-sm mt-6">
          <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">Back to mobile / Google / Facebook</Link>
        </p>
      </div>
    </div>
  );
};

export default LoginIntegrated;
